import {
  Modal,
  InputText,
  TEOLoader,
  Button,
  PersonAvatar,
  Tag,
} from '@teo/components';
import { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGet } from '@/query/fetchHooks';
import {
  Search,
  Eye,
  ChevronDown,
  ChevronUp,
  TimeFilled,
  ChapterFilled,
  ArrowLeft,
} from '@teo/components/icons';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { newUid, convertToTime, nthLastIndexOf, parseCourseType } from '@/util';
import { format } from 'date-fns';
import { PaginationAnything } from '@/components/DataTable/PaginationAnything';
import { CourseTree } from './CourseTree';
import { CourseTreeImport } from './CourseTreeImport';

const CoursesList = ({ closeModal, handleAddCourse, courseType }) => {
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user;
  const { t } = useTranslation();
  const [filterInput, setFilterInput] = useState('');

  const [loadList, setLoadList] = useState(true);
  const [courseSelected, setCourseSelected] = useState(false);
  const [activeCourse, setActiveCourse] = useState();
  const [showLast, setShowLast] = useState(false);
  const [refresh, setRefresh] = useState(newUid(20));
  const [tags, setTags] = useState();

  const examplePath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 2)
  );

  useEffect(() => {
    if (filterInput.trim().length > 0) {
      setRefresh(newUid(20));
    }
  }, [filterInput]);

  useEffect(() => {
    if (activeCourse?.tag) {
      const data = activeCourse?.tag?.split(';;');
      const filteredTags = data.filter(
        (item) => item.toLowerCase() !== activeCourse?.name?.toLowerCase()
      );

      setTags(
        filteredTags
          ?.filter((x) => x?.startsWith('tag:'))
          ?.map((el) => el?.split('tag:')[1])
      );
    }
  }, [activeCourse?.tag]);

  return (
    <Modal isOpen={true}>
      <div className={`${courseSelected && 'hidden'} `}>
        <Modal.Header onClose={closeModal}>
          {courseType === 'course'
            ? t('pages.learning_path.btn_add_course')
            : courseType === 'assessment'
            ? t('pages.learning_path.btn_add_assessment')
            : t('pages.learning_path.btn_add_vacancy')}
        </Modal.Header>
        <Modal.Body>
          <div className="-mx-6 max-h-[70vh] overflow-y-scroll px-6">
            <div className="pb-5">
              <InputText
                value={filterInput}
                onChange={(ev) => {
                  setFilterInput(ev.target.value);
                  setLoadList(true);
                }}
                Icon={Search}
                placeholder={t(
                  'pages.create_edit_course.library.placeholder_search'
                )}
              />
            </div>

            <div className="flex flex-row items-center">
              <h4 className="mb-2 text-base">
                {courseType === 'course'
                  ? t('links.courses')
                  : courseType === 'assessment'
                  ? t('links.assessments')
                  : t('links.vacancies')}
              </h4>

              <span
                className="ml-auto flex cursor-pointer items-center text-xs"
                onClick={() => setShowLast(!showLast)}
              >
                {t('pages.create_edit_course.library.latest_btn')}
                {showLast ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </span>
            </div>
            <div className="mt-3 flex w-full max-w-6xl flex-col flex-wrap gap-2 md:gap-3 lg:gap-2">
              {loadList && (
                <div className="mx-auto p-10">
                  <TEOLoader
                    loaderSrc="/images/TEOloader.gif"
                    className="w-[100px]"
                  />
                </div>
              )}

              <PaginationAnything
                key={refresh}
                className={`mt-3 -ml-2 -mr-2 flex min-h-[600px] min-w-full flex-wrap content-baseline ${
                  loadList === true && ' hidden '
                }`}
                fetch={useGet.bind(null, '/myactivecoursecontents', {
                  userId: authUser?.id,
                  masterId: 'null',
                  publicationCourseType: courseType,
                  onSuccess: (result) => {
                    if (result.data) {
                      setLoadList(false);
                    }
                  },
                })}
                defaultSort={
                  showLast ? [{ id: 'createdAt' }] : [{ id: '-createdAt' }]
                }
                empty={{
                  template: 2,
                  loadContent: loadList,
                  text: t('pages.create_library.library_empty'),
                }}
                page={0}
                pageSize={12}
                filter={filterInput}
                render={(course) => (
                  <div
                    key={course?.id}
                    className=" basis-1/2 overflow-hidden px-2 pb-2 md:basis-1/3 lg:basis-1/4"
                  >
                    <div
                      className="group cursor-pointer pb-4"
                      onClick={async () => {
                        setActiveCourse(course);
                        setCourseSelected(true);
                      }}
                    >
                      <div className="flex flex-col">
                        <div className="relative mb-2 overflow-hidden rounded-lg">
                          <img
                            src={
                              course?.courseImage
                                ? course?.courseImage
                                : course?.image
                                ? course?.image
                                : '/assets/images/opleidingen.jpg'
                            }
                            alt={
                              course?.courseName
                                ? course?.courseName
                                : course?.name
                            }
                            className="h-28 w-full rounded-md object-cover"
                          />
                          {(course?.organisationId === 18 ||
                            course?.organisationId == null) && (
                            <img
                              src="/images/TEO_logo.svg"
                              className="absolute bottom-1 right-1 h-8 w-8"
                            />
                          )}
                          <div className="absolute inset-0 h-full w-full rounded-lg group-hover:backdrop-blur-sm"></div>
                          <span className="absolute inset-0 hidden items-center justify-center group-hover:flex">
                            <span className="rounded-md bg-grey-01 p-1 text-grey-09">
                              <Eye className="h-5 w-5 "></Eye>
                            </span>
                          </span>
                        </div>

                        <h3 className="text-base-semibold mb-2 break-words">
                          {course?.courseName
                            ? course?.courseName
                            : course?.name}
                        </h3>
                        <div className="flex flex-row items-center gap-3 text-sm text-grey-04">
                          <div className="mr-3 flex">
                            <ChapterFilled className="mr-1 w-4" />

                            <span>
                              {course?.nrOfDirectories ? (
                                <> {course?.nrOfDirectories} </>
                              ) : (
                                <>0</>
                              )}
                            </span>
                          </div>

                          {course?.totalEstimatedTime > 0 && (
                            <div className="flex">
                              <TimeFilled className="mr-1 w-4" />
                              <span>
                                {convertToTime(
                                  Math.abs(course?.totalEstimatedTime),
                                  t('pages.learn_index.hour'),
                                  t('pages.learn_index.minute')
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
        </Modal.Body>
      </div>

      {courseSelected && (
        <div>
          <Modal.Header onClose={closeModal}>
            <div className="flex w-full flex-row pr-3">
              <Button
                className="flex items-center gap-3"
                variant="text"
                onClick={() => setCourseSelected(false)}
              >
                <ArrowLeft className="h-5 w-5" />
                <p>{t('pages.create_edit_course.library.back_library')}</p>
              </Button>
              <div className="ml-auto flex flex-row gap-3">
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      window.open(
                        activeCourse?.publicationType === 'teo'
                          ? `${examplePath}/${parseCourseType(
                              activeCourse?.publicationCourseType
                            )}/${activeCourse?.id}/preview?les=0&new_tab=true`
                          : `${examplePath}/${parseCourseType(
                              activeCourse?.publicationCourseType
                            )}/${activeCourse?.id}`,
                        '_blank'
                      );
                    }}
                  >
                    {t('pages.create_edit_course.library.preview')}
                  </Button>

                  <Button
                    variant="primary"
                    onClick={() => handleAddCourse(activeCourse)}
                  >
                    {courseType === 'course'
                      ? t('pages.learning_path.btn_add_course')
                      : courseType === 'assessment'
                      ? t('pages.learning_path.btn_add_assessment')
                      : t('pages.learning_path.btn_add_vacancy')}
                  </Button>
                </>
              </div>
            </div>
          </Modal.Header>
          <div className="max-h-[95vh] min-h-max p-6">
            <div className="flex flex-col">
              <div className="mb-6">
                <img
                  src={
                    activeCourse?.courseImage
                      ? activeCourse?.courseImage
                      : activeCourse?.image
                      ? activeCourse?.image
                      : '/assets/images/opleidingen.jpg'
                  }
                  alt={activeCourse?.courseName || activeCourse?.name}
                  className="max-h-60 w-full rounded-md object-cover"
                />
              </div>
              <div className="flex flex-col">
                <h1 className="mb-6 text-xl">
                  <span className="pr-2 font-normal text-grey-06">
                    {courseType === 'course'
                      ? t('links.course')
                      : courseType === 'assessment'
                      ? t('links.assessment')
                      : t('links.vacancy')}
                    :
                  </span>
                  {activeCourse?.courseName || activeCourse?.name}
                </h1>
                <div className="mb-6 flex flex-wrap items-center">
                  <PersonAvatar
                    inline
                    id={activeCourse?.authorId}
                    name={activeCourse?.username}
                    imageSrc={
                      activeCourse?.picture ? activeCourse?.picture : ''
                    }
                  ></PersonAvatar>
                  <div>
                    <p className="text-xs text-grey-07">
                      {activeCourse?.username && (
                        <>
                          {t('pages.create_edit_course.library.written_by')}{' '}
                          {activeCourse?.username}
                        </>
                      )}
                      <span className="-mt-1 px-2">|</span>
                    </p>
                  </div>
                  <p className="text-xs text-grey-07">
                    {activeCourse?.updatedAt &&
                      t('pages.create_edit_course.library.last_updated') +
                        ' ' +
                        format(
                          new Date(activeCourse?.updatedAt),
                          'dd MMM yyyy'
                        )}
                  </p>
                  {activeCourse?.totalEstimatedTime > 0 && (
                    <span className="flex flex-row items-center gap-1 text-xs text-grey-07">
                      <span className="-mt-1 px-2">|</span>
                      <p>
                        <span className="pr-1">
                          {t('pages.create_edit_course.library.reading_time')}
                        </span>
                        {convertToTime(
                          Math.abs(activeCourse?.totalEstimatedTime),
                          t('pages.learn_index.hour'),
                          t('pages.learn_index.minute')
                        )}
                      </p>
                    </span>
                  )}
                </div>
                {tags?.length > 0 && (
                  <div className="mb-5 flex flex-wrap items-center gap-3">
                    <Tag className="h-5 w-5 text-grey-07" />
                    {tags?.map((el, i) => {
                      return (
                        <div
                          key={i}
                          className="inline-flex items-center gap-1 rounded bg-grey-transparent-02 py-0.5 px-2 text-sm font-medium text-grey-08"
                        >
                          {el}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {activeCourse?.description && (
                <p
                  className="mb-6 text-sm text-[#4D4D50]"
                  dangerouslySetInnerHTML={{
                    __html: activeCourse?.description,
                  }}
                ></p>
              )}
              <div>
                <p className="mb-3 text-sm font-semibold">
                  {t('extra_info.contents')}
                </p>
                <div className="rounded-lg border border-grey-02">
                  {activeCourse?.publicationType === 'scorm' ? (
                    <CourseTreeImport activeCourse={activeCourse} />
                  ) : (
                    <CourseTree activeCourse={activeCourse} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CoursesList;
